import React from "react"
import { StyleSheet, Text, TouchableOpacity, View } from "react-native"
import moment from "moment"
import "moment/locale/fr"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"
import { elide } from "@common/utils/activationSuiteDisplay"
import FunnelStepBullet from "@common/components/funnel/FunnelStepBullet"
import FunnelChoiceChip from "@common/components/funnel/FunnelChoiceChip"
import FunnelSubjectChips from "@common/components/funnel/FunnelSubjectChips"
import FunnelGoalDial from "@common/components/funnel/FunnelGoalDial"
import FunnelToFix from "@common/components/funnel/FunnelToFix"
import FunnelSubjectHoursSplit from "@common/components/funnel/FunnelSubjectHoursSplit"
import FunnelReveal from "@common/components/funnel/FunnelReveal"
import { formatEuros } from "../../brevet/utils/formatEuros"
import { capitalize } from "../../summer/constants/summerContent"
import {
    INSCRIPTION_LYCEE_RULE,
    INSCRIPTION_QUESTIONS,
    firstChargeWhen,
    formatSlotsShort,
} from "../constants/inscriptionContent"
import { COMMITMENT_COPY } from "../constants/inscriptionFormats"
import CommitmentSwitch from "./CommitmentSwitch"
import FormatTiles from "./FormatTiles"
import StartOptionCards from "./StartOptionCards"

/**
 * Le compositeur de la formule Rentrée — six questions empilées dans un
 * écran, dans l'ordre où le parent les pose :
 *   1. les MATIÈRES ;
 *   2. l'OBJECTIF visé sur chacune ;
 *   3. le VOLUME hebdomadaire, et sa répartition entre matières ;
 *   4. les DISPONIBILITÉS de l'élève ;
 *   5. la DATE de démarrage — deux réponses toutes faites, ou une autre date ;
 *   6. le FORMAT de cours (Solo, Tandem) — en dernier, l'ordre de
 *      l'écran d'abonnement dont ce compositeur est le jumeau — puis
 *      l'interrupteur d'ENGAGEMENT, quand le serveur vend sans.
 * Les questions 2 à 6 n'apparaissent qu'une fois la première renseignée : le
 * parent découvre l'écran par paliers au lieu de le prendre en bloc.
 *
 * Tout se coche SUR l'écran — plus de modale pour voir les matières ni les
 * créneaux. Le parent ne saisit jamais un nombre d'heures par matière : la
 * répartition est automatique et se lit en clair ; « Répartir autrement » est
 * le seul recours. Aucun montant n'est calculé ici : le devis vient du
 * serveur et se relit tel quel.
 *
 * Purement présentationnel — tout l'état vit dans
 * {@see InscriptionConfigurationScreen}.
 */
const InscriptionComposer = ({
    firstname,
    // 1 — matières
    subjectOptions = [],
    subjectById,
    subjectIds = [],
    maxSubjects,
    /** Verdict de l'écran (lycée : plus de matières que d'heures). */
    subjectsExceedHours = false,
    subjectsLoading = false,
    onToggleSubject,
    // 2 — objectif par matière
    subjectGoals = {},
    onSetGoal,
    // 3 — volume
    hoursOptions = [],
    weeklyHours,
    /** Volume plancher qui couvre les matières (lycée) : en dessous, la chip se grise. */
    minHoursForSubjects = 0,
    /** Le seul volume qui se choisit une fois l'engagement coupé. */
    hoursLockedTo = null,
    onSetHours,
    isHomeworkSupport = false,
    hoursBySubject = {},
    canRedistribute = false,
    onOpenRedistribute,
    // 4 — disponibilités
    slots = [],
    onOpenSlots,
    minSlots = 1,
    // 5 — date
    startDate,
    quote,
    startDateAdjustedMessage,
    onOpenDate,
    /** Les réponses toutes faites ; vide tant que le devis n'en sert pas. */
    startOptions = [],
    /** `null` : la date vendue n'est aucune des réponses. */
    selectedStartOption = null,
    onPickStartOption,
    // 6 — format de cours ; absent quand il a sa propre page
    showFormat = true,
    formatTiles = [],
    onPickFormat,
    onOpenFormatInfo,
    /** `null` : le serveur ne vend pas sans engagement, pas d'interrupteur. */
    commitment = null,
    onToggleCommitment,
    // devis
    /** L'écran chiffre : faux tant que quelque chose bloque, et pour un
     *  dossier dont le format — donc le prix — se choisit à la page d'après. */
    priced = false,
    serverError,
    quoteHint,
    // refus
    sectionToFix,
    toFixNudge = 0,
    sectionRef,
    onSectionLayout,
}) => {
    const hasSubjects = subjectIds.length > 0
    const missingSlots = Math.max(0, minSlots - slots.length)
    // Sans le format, cette page ne chiffre rien : elle n'annonce pas de tarif.
    const unpricedLabel =
        serverError ||
        quoteHint ||
        (showFormat
            ? "Le tarif s'affiche dès que la formule est complète."
            : null)

    const dateActions = (linkLabel) => (
        <>
            {startDateAdjustedMessage ? (
                <Text style={styles.dateAdjusted}>
                    {startDateAdjustedMessage}
                </Text>
            ) : null}
            <TouchableOpacity
                onPress={onOpenDate}
                accessibilityRole="button"
                style={styles.linkRow}
            >
                <FontAwesome5
                    name="calendar-alt"
                    size={11}
                    color={theme.colors.primary}
                />
                <Text style={styles.link}>{linkLabel}</Text>
            </TouchableOpacity>
        </>
    )

    return (
        <View style={styles.wrap}>
            {/* 1 — les matières */}
            <View
                ref={sectionRef?.("subjects")}
                onLayout={onSectionLayout?.("subjects")}
            />
            <FunnelStepBullet n={1} label={INSCRIPTION_QUESTIONS.subjects} />
            {sectionToFix === "subjects" ? (
                <FunnelToFix
                    nudge={toFixNudge}
                    message={
                        subjectsExceedHours
                            ? `${subjectIds.length} matières pour ${weeklyHours} h : retirez-en ${
                                  subjectIds.length - weeklyHours
                              } ou prenez plus d'heures.`
                            : "Choisissez au moins une matière."
                    }
                />
            ) : null}
            <FunnelSubjectChips
                options={subjectOptions}
                selectedIds={subjectIds}
                maxSubjects={maxSubjects}
                onToggle={onToggleSubject}
                loading={subjectsLoading}
                emptyText="Aucune matière disponible pour cette classe pour le moment."
            />

            <FunnelReveal visible={hasSubjects}>
                {/* 2 — l'objectif, matière par matière */}
                <FunnelStepBullet n={2} label={INSCRIPTION_QUESTIONS.goals} />
                {subjectIds.map((id) => (
                    <FunnelGoalDial
                        key={id}
                        subjectName={subjectById.get(id)?.label ?? "Matière"}
                        value={subjectGoals[id]}
                        onChange={(slug) => onSetGoal?.(id, slug)}
                    />
                ))}

                {/* 3 — le volume */}
                <View
                    ref={sectionRef?.("hours")}
                    onLayout={onSectionLayout?.("hours")}
                />
                <FunnelStepBullet n={3} label={INSCRIPTION_QUESTIONS.hours} />
                {sectionToFix === "hours" ? (
                    <FunnelToFix
                        nudge={toFixNudge}
                        message="Choisissez combien d'heures par semaine."
                    />
                ) : null}
                <View style={styles.chips}>
                    {hoursOptions.map((option) => {
                        const selected = option.value === weeklyHours
                        // Au lycée une matière veut son heure : un volume
                        // qui ne couvre pas les matières choisies se grise.
                        // Sans engagement, tous sauf celui qui se vend.
                        const offCommitment =
                            hoursLockedTo !== null &&
                            option.value !== hoursLockedTo
                        const locked =
                            offCommitment ||
                            (!selected && option.value < minHoursForSubjects)
                        return (
                            <FunnelChoiceChip
                                key={option.value}
                                label={`${option.value} h`}
                                selected={selected}
                                locked={locked}
                                onPress={() => onSetHours?.(option.value)}
                                // Le repère social vit DANS la chip, en
                                // toutes lettres : posé au-dessus, il
                                // passait sous la chip voisine.
                                badge={
                                    option.recommended && !offCommitment ? (
                                        <View style={styles.popular}>
                                            <Text style={styles.popularText}>
                                                Le plus choisi
                                            </Text>
                                        </View>
                                    ) : null
                                }
                            />
                        )
                    })}
                </View>
                {minHoursForSubjects > 1 ? (
                    <Text style={styles.hint}>{INSCRIPTION_LYCEE_RULE}</Text>
                ) : null}
                {isHomeworkSupport ? (
                    <Text style={styles.hint}>
                        {subjectIds.length} matières pour {weeklyHours} h : le
                        masteur fera de l&apos;aide aux devoirs sur
                        l&apos;ensemble de ces matières.
                    </Text>
                ) : null}
                {weeklyHours > 0 &&
                subjectIds.length > 1 &&
                !isHomeworkSupport ? (
                    <View style={styles.split}>
                        <FunnelSubjectHoursSplit
                            subjectIds={subjectIds}
                            subjectById={subjectById}
                            hoursBySubject={hoursBySubject}
                            canRedistribute={canRedistribute}
                            onOpenRedistribute={onOpenRedistribute}
                        />
                    </View>
                ) : null}

                {/* 4 — les disponibilités */}
                <View
                    ref={sectionRef?.("slots")}
                    onLayout={onSectionLayout?.("slots")}
                />
                <FunnelStepBullet
                    n={4}
                    label={INSCRIPTION_QUESTIONS.slots(firstname)}
                />
                <Text style={styles.hint}>
                    {weeklyHours > 0
                        ? `Au moins ${minSlots} créneau${minSlots > 1 ? "x" : ""} pour ${weeklyHours} h — plus vous en donnez, mieux on place ${firstname}.`
                        : `Cochez toutes les disponibilités ${elide(firstname)} : plus vous en donnez, mieux on place ${firstname}.`}
                </Text>
                {sectionToFix === "slots" ? (
                    <FunnelToFix
                        nudge={toFixNudge}
                        message={
                            slots.length === 0
                                ? `Cochez au moins ${minSlots} créneau${minSlots > 1 ? "x" : ""} : c'est ce qui permet de placer ${firstname}.`
                                : `Il manque ${missingSlots} créneau${missingSlots > 1 ? "x" : ""} : vous prenez ${weeklyHours} h par semaine.`
                        }
                    />
                ) : null}
                {/* Les créneaux se cochent dans la modale existante (la grille
                        partagée) : posée dans l'écran, elle prenait l'écran
                        entier et repoussait la date et le prix hors de vue.
                        L'écran garde le résumé — c'est lui qui se relit. */}
                <View style={styles.dateCard}>
                    <Text style={styles.dateValue}>
                        {slots.length > 0
                            ? `${slots.length} créneau${slots.length > 1 ? "x" : ""}`
                            : "Aucun créneau choisi"}
                    </Text>
                    {slots.length > 0 ? (
                        <Text style={styles.dateSub}>
                            {formatSlotsShort(slots)}
                        </Text>
                    ) : null}
                    <TouchableOpacity
                        onPress={onOpenSlots}
                        accessibilityRole="button"
                        style={styles.linkRow}
                    >
                        <FontAwesome5
                            name="calendar-check"
                            size={11}
                            color={theme.colors.primary}
                        />
                        <Text style={styles.link}>
                            {slots.length > 0
                                ? "Modifier les créneaux"
                                : "Choisir les créneaux"}
                        </Text>
                    </TouchableOpacity>
                </View>

                {/* 5 — la date : les réponses toutes faites du devis, et la
                        date du calendrier quand ce n'est aucune d'elles. Sans
                        ces réponses (pas encore de devis, ou un devis gardé
                        d'avant), la date se lit et se change comme avant. */}
                <FunnelStepBullet n={5} label={INSCRIPTION_QUESTIONS.start} />
                {startOptions.length > 0 ? (
                    <>
                        <StartOptionCards
                            options={startOptions}
                            selectedKey={selectedStartOption}
                            onSelect={onPickStartOption}
                        />
                        {startDate && !selectedStartOption ? (
                            <DateCard
                                selected
                                startDate={startDate}
                                firstSession={quote?.first_session_label}
                            >
                                {priced &&
                                Number.isFinite(quote.first_charge_cents) ? (
                                    <Text style={styles.dateSub}>
                                        {`${formatEuros(quote.first_charge_cents / 100)} ${firstChargeWhen(quote, "au premier cours")}`}
                                    </Text>
                                ) : null}
                            </DateCard>
                        ) : null}
                        {dateActions("Une autre date")}
                    </>
                ) : (
                    <DateCard
                        startDate={startDate}
                        firstSession={quote?.first_session_label}
                        fallback={
                            weeklyHours > 0 && slots.length > 0
                                ? "Le premier cours se cale sur vos disponibilités."
                                : "Le premier cours se calera sur vos disponibilités."
                        }
                    >
                        {dateActions("Changer la date")}
                    </DateCard>
                )}

                {showFormat ? (
                    <>
                        {/* 6 — le format : en dernier, comme sur l'écran d'abonnement
                            dont ce compositeur est le jumeau — il se choisit une
                            fois la formule posée, juste avant le prix. Les tuiles
                            se partagent la largeur : tout se lit sans
                            scroller. */}
                        <FunnelStepBullet
                            n={6}
                            label={INSCRIPTION_QUESTIONS.format}
                        />
                        <FormatTiles
                            tiles={formatTiles}
                            lockedLabel={COMMITMENT_COPY.lockedTile}
                            onPick={onPickFormat}
                            onOpenInfo={onOpenFormatInfo}
                        />
                    </>
                ) : null}

                {commitment ? (
                    <View style={styles.commitment}>
                        <CommitmentSwitch
                            label={COMMITMENT_COPY.switchLabel}
                            savingLabel={commitment.savingLabel}
                            checked={commitment.checked}
                            pending={commitment.pending}
                            onToggle={onToggleCommitment}
                        />
                    </View>
                ) : null}

                {/* Le prix — celui du devis serveur, jamais recalculé ici.
                        Enregistré comme cible : un blocage « devis » y renvoie. */}
                <View
                    ref={sectionRef?.("quote")}
                    onLayout={onSectionLayout?.("quote")}
                />
                {sectionToFix === "quote" ? (
                    <FunnelToFix
                        nudge={toFixNudge}
                        message={
                            serverError ||
                            "Le tarif n'a pas encore pu être calculé."
                        }
                    />
                ) : null}
                {/* Ce que le démarrage fait régler se lit sur les réponses
                        de « Début des cours », la mensualité dans le bouton :
                        les répéter ici noyait le parent sous les montants. Les
                        deux échéances ne reviennent que si le serveur ne sert
                        pas ces réponses. */}
                {priced ? (
                    startOptions.length > 0 ? null : (
                        <View style={styles.priceCard}>
                            <PriceLine
                                label="Aujourd'hui, pour réserver"
                                value={formatEuros(quote.deposit_cents / 100)}
                                strong
                            />
                            <PriceLine
                                label={`Le ${moment(quote.first_session_at).locale("fr").format("D MMMM")}`}
                                // Sans le mois (devis d'avant sa mise en ligne,
                                // relu depuis le cache du navigateur), la ligne
                                // se tait plutôt que de dater dans le vide.
                                hint={
                                    quote.first_session_month
                                        ? `${quote.first_month_hours} h de cours en ${quote.first_session_month}`
                                        : null
                                }
                                value={formatEuros(
                                    quote.first_charge_cents / 100
                                )}
                            />
                        </View>
                    )
                ) : unpricedLabel ? (
                    <Text style={styles.emptyPrice}>{unpricedLabel}</Text>
                ) : null}
            </FunnelReveal>
        </View>
    )
}

/**
 * La date de démarrage en toutes lettres, et le premier cours qu'elle donne —
 * à défaut, `fallback`. `selected` : c'est la date vendue, parmi d'autres
 * réponses.
 */
const DateCard = ({
    startDate,
    firstSession,
    fallback,
    selected,
    children,
}) => (
    <View style={[styles.dateCard, selected && styles.dateCardOn]}>
        <Text style={styles.dateValue}>
            {startDate
                ? capitalize(
                      moment(startDate).locale("fr").format("dddd D MMMM")
                  )
                : "Dès que possible"}
        </Text>
        {firstSession || fallback ? (
            <Text style={styles.dateSub}>
                {firstSession ? `Premier cours : ${firstSession}` : fallback}
            </Text>
        ) : null}
        {children}
    </View>
)

/** Une ligne du bloc de prix : intitulé, précision optionnelle, montant. */
const PriceLine = ({ label, hint, value, strong = false }) => (
    <View style={styles.priceRow}>
        <View style={styles.priceLabel}>
            <Text style={styles.priceLabelText}>{label}</Text>
            {hint ? <Text style={styles.priceHint}>{hint}</Text> : null}
        </View>
        <Text style={strong ? styles.priceValueStrong : styles.priceValue}>
            {value}
        </Text>
    </View>
)

const styles = StyleSheet.create({
    wrap: { gap: 10 },
    chips: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
    popular: {
        backgroundColor: theme.colors.accent,
        borderRadius: 999,
        paddingHorizontal: 7,
        paddingVertical: 2,
        marginLeft: 2,
    },
    popularText: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 10,
        color: theme.colors.text,
    },
    hint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        lineHeight: 16,
        marginTop: 8,
    },
    commitment: {
        marginTop: 16,
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        borderRadius: 12,
        paddingVertical: 12,
        paddingHorizontal: 12,
    },
    // Discret mais trouvable : la couleur de la marque, une icône, des mots.
    linkRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginTop: 10,
        alignSelf: "flex-start",
    },
    link: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.primary,
        textDecorationLine: "underline",
    },
    split: { marginTop: 12 },
    // La grille porte sa propre gouttière de 10 px : on la compense pour que
    // ses cartes s'alignent sur les chips.
    dateCard: {
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        borderRadius: 12,
        paddingVertical: 12,
        paddingHorizontal: 14,
        gap: 3,
    },
    dateCardOn: {
        marginTop: 8,
        borderWidth: 2,
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    dateValue: {
        fontFamily: "Montserrat-Bold",
        fontSize: 15,
        color: theme.colors.primaryDark || theme.colors.primary,
    },
    dateSub: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
    },
    dateAdjusted: {
        fontFamily: "Montserrat-Medium",
        fontSize: 12,
        color: theme.colors.warning,
        marginTop: 2,
    },
    priceCard: {
        marginTop: 16,
        padding: 16,
        borderRadius: 12,
        backgroundColor: theme.colors.whiteAlmost,
        gap: 8,
    },
    priceRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between",
    },
    priceLabel: { flex: 1, paddingRight: 10 },
    priceLabelText: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
    },
    priceHint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.light,
        marginTop: 2,
    },
    priceValue: {
        fontFamily: "Montserrat-Bold",
        fontSize: 14,
        color: theme.colors.primaryDark || theme.colors.primary,
    },
    priceValueStrong: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 18,
        color: theme.colors.primary,
    },
    emptyPrice: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
        textAlign: "center",
        marginTop: 16,
    },
})

export default InscriptionComposer
